@if (loading) {
  <p class="empty">{{ 'admin.twitchBots.loading' | adminTranslate }}</p>
} @else if (overview; as data) {
  <section class="panel">
    <header class="panel__header">
      <div>
        <h1>{{ 'admin.twitchBots.title' | adminTranslate }}</h1>
        <p class="panel__lede">{{ 'admin.twitchBots.lede' | adminTranslate }}</p>
      </div>

      <div class="panel__actions">
        <ui-button variant="secondary" [routerLink]="['/schedulers']">
          {{ 'admin.twitchBots.openSchedulers' | adminTranslate }}
        </ui-button>
        <ui-button [disabled]="refreshing" (click)="refreshRegistry()">
          {{ 'admin.twitchBots.refreshNow' | adminTranslate }}
        </ui-button>
      </div>
    </header>

    <div class="summary-grid">
      <article class="summary-card">
        <span class="summary-card__label">{{ 'admin.twitchBots.summary.count' | adminTranslate }}</span>
        <strong>{{ data.entryCount }}</strong>
      </article>

      <article class="summary-card">
        <span class="summary-card__label">{{ 'admin.twitchBots.summary.lastSuccess' | adminTranslate }}</span>
        <strong>{{ data.lastRefreshSuccessAt ? (data.lastRefreshSuccessAt | date:'dd/MM/yyyy HH:mm:ss') : '-' }}</strong>
      </article>

      <article class="summary-card">
        <span class="summary-card__label">{{ 'admin.twitchBots.summary.sourceExpires' | adminTranslate }}</span>
        <strong>{{ data.sourceExpiresAt ? (data.sourceExpiresAt | date:'dd/MM/yyyy HH:mm:ss') : '-' }}</strong>
      </article>

      <article class="summary-card">
        <span class="summary-card__label">{{ 'admin.twitchBots.summary.status' | adminTranslate }}</span>
        <span [class]="statusBadgeClass(data.lastRefreshStatus)">
          {{ statusLabelKey(data.lastRefreshStatus) | adminTranslate }}
        </span>
      </article>
    </div>

    @if (data.scheduler) {
      <section class="scheduler-card">
        <div class="scheduler-card__main">
          <strong>{{ data.scheduler.displayName }}</strong>
          <p>{{ data.scheduler.description }}</p>
        </div>
        <div class="scheduler-card__meta">
          <span>{{ 'admin.twitchBots.scheduler.nextRun' | adminTranslate }}: {{ data.scheduler.nextRunAt ? (data.scheduler.nextRunAt | date:'dd/MM/yyyy HH:mm:ss') : '-' }}</span>
          <span>{{ 'admin.twitchBots.scheduler.frequency' | adminTranslate }}: {{ ('admin.schedulers.frequency.' + data.scheduler.frequency) | adminTranslate }}</span>
        </div>
      </section>
    }

    @if (data.lastRefreshError) {
      <p class="error-banner">
        {{ 'admin.twitchBots.summary.lastError' | adminTranslate }}: {{ data.lastRefreshError }}
      </p>
    }

    <div class="table-wrap">
      <table class="bot-table">
        <thead>
          <tr>
            <th>{{ 'admin.twitchBots.table.login' | adminTranslate }}</th>
            <th>{{ 'admin.twitchBots.table.source' | adminTranslate }}</th>
            <th>{{ 'admin.twitchBots.table.syncedAt' | adminTranslate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (entry of pagedEntries; track entry.login) {
            <tr>
              <td><strong>{{ entry.login }}</strong></td>
              <td>{{ entry.source }}</td>
              <td>{{ entry.syncedAt ? (entry.syncedAt | date:'dd/MM/yyyy HH:mm:ss') : '-' }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <section class="routing-section">
      <div class="routing-section__head">
        <div>
          <h2>{{ 'admin.twitchBots.chatRouting.title' | adminTranslate }}</h2>
          <p class="panel__lede">{{ 'admin.twitchBots.chatRouting.description' | adminTranslate }}</p>
        </div>
      </div>

      <div class="table-wrap">
        <table class="bot-table">
          <thead>
            <tr>
              <th>{{ 'admin.twitchBots.chatRouting.table.broadcasterId' | adminTranslate }}</th>
              <th>{{ 'admin.twitchBots.chatRouting.table.freyrUserId' | adminTranslate }}</th>
              <th>{{ 'admin.twitchBots.chatRouting.table.eventsub' | adminTranslate }}</th>
              <th>{{ 'admin.twitchBots.chatRouting.table.strict' | adminTranslate }}</th>
              <th>{{ 'admin.twitchBots.chatRouting.table.diagnostic' | adminTranslate }}</th>
              <th>{{ 'admin.twitchBots.chatRouting.table.runtime' | adminTranslate }}</th>
              <th>{{ 'admin.twitchBots.chatRouting.table.actions' | adminTranslate }}</th>
            </tr>
          </thead>
          <tbody>
            @if (routingEntries.length === 0) {
              <tr>
                <td colspan="7" class="table-empty">{{ 'admin.twitchBots.chatRouting.empty' | adminTranslate }}</td>
              </tr>
            } @else {
              @for (entry of routingEntries; track entry.broadcasterId) {
                <tr>
                  <td><strong>{{ entry.broadcasterId }}</strong></td>
                  <td>{{ entry.freyrUserId || '-' }}</td>
                  <td>
                    <span [class]="strictModeBadgeClass(entry.chatCommandsViaEventsub)">
                      {{ (entry.chatCommandsViaEventsub ? 'admin.twitchBots.chatRouting.eventsubOn' : 'admin.twitchBots.chatRouting.eventsubOff') | adminTranslate }}
                    </span>
                  </td>
                  <td>
                    <span [class]="strictModeBadgeClass(entry.chatbotStrictHelixOnly)">
                      {{ (entry.chatbotStrictHelixOnly ? 'admin.twitchBots.chatRouting.strictOn' : 'admin.twitchBots.chatRouting.strictOff') | adminTranslate }}
                    </span>
                  </td>
                  <td>
                    <div class="diagnostic-cell">
                      <span [class]="chatbotLevelBadgeClass(entry)">
                        {{ chatbotLevelLabelKey(entry) | adminTranslate }}
                      </span>
                      <strong>{{ complianceStatusKey(entry) | adminTranslate }}</strong>
                      <p>{{ complianceDescriptionKey(entry) | adminTranslate }}</p>
                      <p class="diagnostic-cell__next-step">
                        {{ 'admin.twitchBots.chatRouting.nextStep.label' | adminTranslate }}:
                        {{ nextStepKey(entry) | adminTranslate }}
                      </p>
                    </div>
                  </td>
                  <td>
                    <div class="runtime-cell">
                      <span>{{ 'admin.twitchBots.chatRouting.runtime.channel' | adminTranslate }}: {{ entry.channelLogin || '-' }}</span>
                      <span>{{ 'admin.twitchBots.chatRouting.runtime.bot' | adminTranslate }}: {{ (entry.botRegistered ? 'admin.twitchBots.chatRouting.runtime.registered' : 'admin.twitchBots.chatRouting.runtime.notRegistered') | adminTranslate }}</span>
                      <span>{{ 'admin.twitchBots.chatRouting.runtime.connection' | adminTranslate }}: {{ (entry.botConnected ? 'admin.twitchBots.chatRouting.runtime.connected' : 'admin.twitchBots.chatRouting.runtime.disconnected') | adminTranslate }}</span>
                      <span>{{ 'admin.twitchBots.chatRouting.runtime.delivery' | adminTranslate }}: {{ deliveryModeKey(entry) | adminTranslate }}</span>
                      <span>{{ 'admin.twitchBots.chatRouting.runtime.sender' | adminTranslate }}: {{ entry.senderUserIdAvailable ? (entry.senderUserId || '-') : '-' }}</span>
                    </div>
                  </td>
                  <td class="routing-actions">
                    <ui-button
                      variant="secondary"
                      size="sm"
                      [disabled]="entry.chatbotStrictHelixOnly || updatingStrictByBroadcasterId[entry.broadcasterId] || !canEnableStrictMode(entry)"
                      (click)="updateStrictMode(entry, true)">
                      {{ 'admin.twitchBots.chatRouting.enableStrict' | adminTranslate }}
                    </ui-button>
                    <ui-button
                      variant="secondary"
                      size="sm"
                      [disabled]="!entry.chatbotStrictHelixOnly || updatingStrictByBroadcasterId[entry.broadcasterId]"
                      (click)="updateStrictMode(entry, false)">
                      {{ 'admin.twitchBots.chatRouting.disableStrict' | adminTranslate }}
                    </ui-button>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    </section>

    @if (data.entries.length > 0) {
      <div class="pagination">
        <span class="pagination__status">
          {{ 'admin.twitchBots.pagination.status' | adminTranslate:{ start: paginationStart, end: paginationEnd, total: data.entries.length, page: currentPage, totalPages: totalPages } }}
        </span>
        <div class="pagination__actions">
          <ui-button variant="secondary" size="sm" (click)="previousPage()" [disabled]="currentPage <= 1">
            {{ 'admin.twitchBots.pagination.previous' | adminTranslate }}
          </ui-button>
          <ui-button variant="secondary" size="sm" (click)="nextPage()" [disabled]="currentPage >= totalPages">
            {{ 'admin.twitchBots.pagination.next' | adminTranslate }}
          </ui-button>
        </div>
      </div>
    }
  </section>
}
